import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {CommentBasicModel} from 'app/core/modules/rest/consent/response/comment-basic.model';
import {OutlineUnitBasicModel} from 'app/core/modules/rest/consent/response/outline-unit-basic.model';
import {TranslateService} from '@ngx-translate/core';
import {getLanguageEnumByLanguageCode, LanguageEnum} from 'app/core/enums/language.enum';
import {LevelEnum} from 'app/core/modules/rest/indication/enums/level.enum';
import {RelevanceEnum} from '@core/modules/rest/indication/enums/relevance.enum';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {ChangeUnitOrderModel} from '@core/modules/rest/informationunit/request/change-unit-order.model';
import {SelectMenuModel} from '@shared/components/select-menu/model/select-menu.model';

@Component({
    selector: 'app-cms-outline',
    templateUrl: './cms-outline.component.html',
    styleUrls: ['./cms-outline.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsOutlineComponent implements OnInit, OnChanges {
    @Input() treeData: OutlineUnitBasicModel[] | undefined;
    @Input() currentUnitOrderId: number | undefined;
    @Input() listClickable = false;
    @Input() comments: CommentBasicModel[] | undefined;
    @Output() emitClickedElement = new EventEmitter<OutlineUnitBasicModel>();
    @Output() unitLevelUpEvent = new EventEmitter<OutlineUnitBasicModel>();
    @Output() unitLevelDownEvent = new EventEmitter<OutlineUnitBasicModel>();
    @Output() moveUnitEvent = new EventEmitter<ChangeUnitOrderModel>();
    @Output() unitDeleteEvent = new EventEmitter<OutlineUnitBasicModel>();
    @Output() unitRelevanceEvent = new EventEmitter<{ event: RelevanceEnum, unit: OutlineUnitBasicModel }>();

    appLanguageString: string;
    appLanguage: LanguageEnum;
    noTitle: string;
    relevanceList: SelectMenuModel[] = [];

    constructor(private translate: TranslateService) {
    }

    ngOnInit(): void {
        this.appLanguage = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode'));
        if (this.appLanguage === LanguageEnum.GERMAN) {
            this.appLanguageString = this.translate.instant('GENERAL.GERMAN');
        } else if (this.appLanguage === LanguageEnum.SERBIAN) {
            this.appLanguageString = this.translate.instant('GENERAL.SERBIAN');
        } else {
            this.appLanguageString = this.translate.instant('GENERAL.ENGLISH');
        }
        this.noTitle = this.translate.instant('GENERAL.UNTITLED') + '*';
        const relevance = Object.values(RelevanceEnum);
        relevance.forEach(x => {
            this.relevanceList.push({
                selectId: x,
                selectValue: x
            });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currentUnitOrderId && this.currentUnitOrderId) {
            this.scrollIntoView();
        }
    }

    elementClicked(unit: OutlineUnitBasicModel): void {
        this.emitClickedElement.emit(unit);
    }

    stripTagsFromString(text: string): string {
        if (text === null) {
            return '';
        } else {
            return text.replace(/(<([^>]+)>)/gi, '');
        }
    }

    private scrollIntoView(): void {
        const currentUnit = document.getElementById(this.currentUnitOrderId.toString());
        if (currentUnit != null) {
            currentUnit.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'center'
            });
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.code === 'Space') {
            event.preventDefault();
        }
    }

    onKeyUp(event: KeyboardEvent, item: OutlineUnitBasicModel): void {
        if (event.key === 'Enter') {
            this.elementClicked(item);
        } else if (event.code === 'Space') {
            event.preventDefault();
        }
    }

    isLevelUpDisabled(item: OutlineUnitBasicModel): boolean {
        if (this.treeData[0].informationUnitId === item.informationUnitId) {
            return true;
        }
        return item.unitLevel === LevelEnum.A;
    }

    isLevelDownDisabled(item: OutlineUnitBasicModel): boolean {
        if (this.treeData[0].informationUnitId === item.informationUnitId) {
            return true;
        } else if (item.unitLevel === LevelEnum.E || item.unitLevel === LevelEnum.F) {
            return true;
        }
        const indexOfUnit = this.treeData.indexOf(item);
        if (indexOfUnit !== -1 && indexOfUnit !== 0) {
            const previousUnit = this.treeData[indexOfUnit - 1];
            const parentOrdinal: number = Object.keys(LevelEnum).indexOf(previousUnit.unitLevel);
            const unitOrdinal: number = Object.keys(LevelEnum).indexOf(item.unitLevel);
            return parentOrdinal - unitOrdinal < 0;
        }
        return false;
    }

    drop(event: CdkDragDrop<OutlineUnitBasicModel>): void {
        const indexes: ChangeUnitOrderModel = {
            previousIndex: event.previousIndex,
            currentIndex: event.currentIndex,
            indicationId: this.treeData[0].indicationId,
            language: this.appLanguage
        };
        this.moveUnitEvent.emit(indexes);
    }

    onChange(event: RelevanceEnum, unit: OutlineUnitBasicModel): void {
        this.unitRelevanceEvent.emit({event, unit});
    }

    trackByFn(index: number, unit: OutlineUnitBasicModel): string {
        return `${index}_${unit.informationUnitId}`;
    }
}
